import React, { useState } from "react";
import {
  CheckCircle2,
  ExternalLink,
  Fingerprint,
  Image as ImageIcon,
  Inbox,
  Link2,
  RefreshCw,
  Sparkles,
  Tag,
  X
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { SearchFilterForm } from "../../components/ui/SearchFilterForm";
import { Card, CardContent } from "../../components/ui/Card";
import { Pagination } from "../../components/ui/Pagination";
import { Badge } from "../../components/ui/Badge";
import { AutoAcceptedBadge } from "../../components/ui/AutoAcceptedBadge";
import Input from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { ProviderLabel } from "../../components/ui/ProviderLabel";
import { EntityLink } from "../../components/ui/EntityLink";
import { ActionButton, AbsentSection } from "../../components/ui/ActionButton";
import { artistResearchApi } from "../../utils/api";
import { displayLabel, retryHint } from "../../utils/helpers";
import { QueueSpinner, StatusTabs, SuccessToast } from "../../components/ui/ResearchQueue";
import { useResearchQueue } from "../../hooks/useResearchQueue";

// No image panels or third-party page reads on this screen, unlike event research — a page is
// just rows — so this is only a row count and can be the normal admin default.
const PAGE_SIZE = 25;

// Mirrors PLATFORM_HOSTS in shared/constants/artists.py (JS cannot import it). The order
// is the one the manual-add picker offers, and it leads with the two that do real work:
// `links.wikidata` and `links.deezer` are the only identities ArtistImageEnricher can fetch an
// image from. The server validates the platform and the host regardless, so a drift here costs a
// missing option, never a bad write.
const PLATFORMS = [
  "wikidata",
  "deezer",
  "homepage",
  "spotify",
  "bandcamp",
  "wikipedia",
  "apple_music",
  "tidal",
  "soundcloud",
  "youtube",
  "youtube_music",
  "instagram",
  "facebook",
  "twitter",
  "tiktok",
  "lastfm",
  "songkick",
  "bandsintown"
];

const PLATFORM_LABELS = {
  apple_music: "Apple Music",
  youtube_music: "YouTube Music",
  lastfm: "Last.fm",
  homepage: "Official site",
  wikidata: "Wikidata",
  wikipedia: "Wikipedia",
  bandsintown: "Bandsintown",
  songkick: "Songkick",
  soundcloud: "SoundCloud",
  tiktok: "TikTok",
  youtube: "YouTube"
};

const platformLabel = (platform) =>
  PLATFORM_LABELS[platform] || String(platform || "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const PLATFORM_OPTIONS = PLATFORMS.map((value) => ({ value, label: platformLabel(value) }));

// The two platforms an accepted link actually unblocks something with. Flagged in the UI because
// "this one gets the artist a picture" is the difference between a nice-to-have and the reason
// the record exists, and it is invisible from the URL alone.
const IMAGE_SOURCE_PLATFORMS = new Set(["wikidata", "deezer"]);

/**
 * Every link shows one, because every link is accepted on its own. Banded rather than raw so the
 * number reads as a recommendation; the bands are the agent's own thresholds.
 */
function Confidence({ value }) {
  if (typeof value !== "number") return null;
  const variant = value >= 0.8 ? "success" : value >= 0.7 ? "secondary" : "warning";
  return (
    <Badge variant={variant} title="How sure the agent is that this page belongs to THIS artist">
      {Math.round(value * 100)}%
    </Badge>
  );
}

// Mirrors LINK_CONFIDENCE_FLOOR in domains/reports/artist_research_record.py.
const LINK_CONFIDENCE_FLOOR = 0.7;

/** The URL itself, which is what a reviewer opens to judge the proposal. */
const ProposedUrl = ({ url }) => (
  <a
    href={url}
    target="_blank"
    rel="noreferrer"
    className="flex items-center gap-1 break-all text-sm text-primary hover:underline"
  >
    {url} <ExternalLink className="h-3 w-3 shrink-0" />
  </a>
);

const AcceptedBadge = ({ label }) => (
  <Badge variant="success">
    <CheckCircle2 className="mr-1 h-3 w-3" /> {label}
  </Badge>
);

/**
 * Why a section has nothing in it — "nothing found", "we could not tell" and "this act has no web
 * presence" are otherwise the same blank space. The run recorded the answer in its markers.
 */
function absentLinksReason(record) {
  const verdict = record.links_check?.verdict;
  if (verdict === "none_exist")
    return "This act has no web presence to find — local or historical, nobody has catalogued it.";
  if (verdict === "no_result") return `Could not confirm any page for this artist${retryHint(record.links_check)}.`;
  if (verdict === "reported") return "Every link it found has been accepted.";
  return "Links were not researched on this pass.";
}

function absentMusicbrainzReason(record) {
  if (record.artist_source_ids?.musicbrainz) return "The artist already has a MusicBrainz id.";
  const verdict = record.musicbrainz_check?.verdict;
  if (verdict === "ambiguous")
    return "Several acts share this name on MusicBrainz and the agent could not separate them — this one needs a human.";
  if (verdict === "no_result") return `No MusicBrainz entry found${retryHint(record.musicbrainz_check)}.`;
  if (verdict === "reported") return "The MusicBrainz id it found has been accepted.";
  return "MusicBrainz was not researched on this pass.";
}


/**
 * Put a link on the artist that the record does not propose — the agent misses plenty, and the
 * alternative is the artists editor, which rewrites the whole `links` map from a form.
 *
 * The platform is picked, not typed: `artist.links` is read by key, so a freelance key is a link
 * nothing ever looks at. The server validates it and the host regardless.
 */
function ManualLink({ record, busy, act }) {
  const [platform, setPlatform] = useState("wikidata");
  const [url, setUrl] = useState("");
  const trimmed = url.trim();

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* `options`, not JSX children: Select renders its own <option> list and its explicit
          children win over anything arriving through the prop spread, so children are silently
          discarded — the picker then shows only the disabled placeholder and every add posts
          whatever the initial state happened to be. Same shape as the event-research price
          currency select. */}
      <Select
        value={platform}
        onChange={(event) => setPlatform(event.target.value)}
        options={PLATFORM_OPTIONS}
        className="w-44"
        aria-label="Link platform"
      />
      <Input
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        placeholder="https://…"
        className="w-72"
        aria-label="Link URL"
      />
      <ActionButton
        busy={busy}
        actionKey={`add-link-${record.id}`}
        disabled={!trimmed}
        onClick={() =>
          act(
            `add-link-${record.id}`,
            async () => {
              await artistResearchApi.addLink(record.id, platform, trimmed);
              setUrl("");
            },
            `Added the ${platformLabel(platform)} link`,
          )
        }
      >
        Add link
      </ActionButton>
    </div>
  );
}

/**
 * Remove one link from the artist, reporting what the server actually did — it may reopen the
 * record, which the admin needs to know, since the card is about to leave the tab they are on.
 */
const removeLink = (record, platform) => async () => {
  const result = await artistResearchApi.removeLink(record.id, platform);
  return result?.message;
};

/** One proposed link: what it is, why the agent believes it, and the accept. */
function ProposedLink({ record, link, busy, act, isOpen }) {
  const accepted = Boolean(link.accepted_at);
  const key = `accept-link-${record.id}-${link.platform}`;

  return (
    <div className="flex flex-wrap items-start justify-between gap-2 rounded-md border p-2">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{platformLabel(link.platform)}</Badge>
          <Confidence value={link.confidence} />
          {IMAGE_SOURCE_PLATFORMS.has(link.platform) && (
            <Badge variant="secondary" title="Accepting this lets image discovery fetch a picture for the artist">
              <ImageIcon className="mr-1 h-3 w-3" /> Image source
            </Badge>
          )}
          {accepted && <AcceptedBadge label="Added" />}
          {accepted && link.auto_accepted && <AutoAcceptedBadge />}
        </div>
        <ProposedUrl url={link.url} />
        {/* The agent's own words, and the only way to judge a link it could not open. */}
        {link.reasoning && <p className="text-xs text-muted-foreground">{link.reasoning}</p>}
      </div>
      {accepted ? (
        <ActionButton
          busy={busy}
          actionKey={`remove-link-${record.id}-${link.platform}`}
          onClick={() =>
            act(
              `remove-link-${record.id}-${link.platform}`,
              removeLink(record, link.platform),
              `Removed the ${platformLabel(link.platform)} link`,
            )
          }
        >
          Remove
        </ActionButton>
      ) : (
        // Only on an open record: every accept goes through `_open_artist_record`, which 409s on
        // a closed one, so rendering the button there offers an action that can only fail.
        // Removal is deliberately NOT gated the same way — it is the undo, and the accept it
        // reverts is what closes the record.
        isOpen && (
          <ActionButton
            busy={busy}
            actionKey={key}
            variant="default"
            onClick={() =>
              act(
                key,
                () => artistResearchApi.acceptLink(record.id, link.url),
                `Added the ${platformLabel(link.platform)} link`
              )
            }
          >
            Add to artist
          </ActionButton>
        )
      )}
    </div>
  );
}

/**
 * The proposed MusicBrainz identity. Weightier than a link accept and presented as such — it
 * re-derives country, genres and links, unblocks classification, and can merge this artist into
 * another — so the disambiguation comment and the reasoning are shown rather than tucked away.
 */
function ProposedMusicbrainz({ record, busy, act, isOpen }) {
  const entry = record.musicbrainz;
  const accepted = Boolean(entry.accepted_at);
  const key = `accept-mb-${record.id}`;

  return (
    <div className="space-y-2 rounded-md border p-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">MusicBrainz</Badge>
        <Confidence value={entry.confidence} />
        {accepted && <AcceptedBadge label="Assigned" />}
        {accepted && entry.auto_accepted && <AutoAcceptedBadge />}
      </div>
      <p className="text-sm">
        {entry.name || "(unnamed entry)"}
        {entry.disambiguation && <span className="text-muted-foreground"> — {entry.disambiguation}</span>}
      </p>
      {entry.url && <ProposedUrl url={entry.url} />}
      {entry.reasoning && <p className="text-xs text-muted-foreground">{entry.reasoning}</p>}
      {!accepted && isOpen && (
        <div className="flex flex-wrap items-center gap-2">
          <ActionButton
            busy={busy}
            actionKey={key}
            variant="default"
            onClick={() =>
              act(
                key,
                // Returning a string overrides the default message. The response carries
                // `merged`/`canonical_id` when the id already belonged to another artist and the
                // two were auto-merged — a real outcome, not an error, and reporting it as the
                // generic success left the admin working a card whose artist no longer exists on
                // its own.
                async () => {
                  const result = await artistResearchApi.acceptMusicbrainz(record.id);
                  return result?.merged
                    ? `That id already belonged to another artist — the two were merged into ${result.canonical_id}`
                    : undefined;
                },
                "Assigned the MusicBrainz id — country, genres, links, image and classification follow"
              )
            }
          >
            Assign to artist
          </ActionButton>
          <span className="text-xs text-muted-foreground">
            Re-enriches country, genres and links from this identity, then runs image discovery and
            classification.
          </span>
        </div>
      )}
    </div>
  );
}

/** What we already know about the artist — the evidence a link is judged against. */
function ArtistFacts({ record, busy, act }) {
  const held = Object.entries(record.artist_links || {}).filter(([, url]) => url);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {record.artist_country && <Badge variant="outline">{record.artist_country}</Badge>}
        <span>
          {record.artist_event_count} event{record.artist_event_count === 1 ? "" : "s"}
        </span>
        <span>{record.artist_has_image ? "Has an image" : "No image"}</span>
        <span>{record.artist_has_classification ? "Classified" : "Not classified"}</span>
      </div>
      {record.artist_genres?.length > 0 && (
        <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          <Tag className="h-3 w-3" />
          {record.artist_genres.join(", ")}
        </p>
      )}
      {held.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          <span>Already holds:</span>
          {/* Each one removable, which is what makes `remove-link` the undo for BOTH add paths.
              A manually added link stamps nothing on the record, so it never appears
              as a proposal. The endpoint accepts any key by design, so this also reaches links
              our own pipeline wrote. */}
          {held.map(([platform, url]) => (
            <span key={platform} className="inline-flex items-center gap-1 rounded border px-1">
              <a href={url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                {platformLabel(platform)}
              </a>
              <ActionButton
                busy={busy}
                actionKey={`held-${record.id}-${platform}`}
                className="h-4 w-4 p-0"
                aria-label={`Remove the ${platformLabel(platform)} link from this artist`}
                onClick={() =>
                  act(
                    `held-${record.id}-${platform}`,
                    removeLink(record, platform),
                    `Removed the ${platformLabel(platform)} link`,
                  )
                }
              >
                <X className="h-3 w-3" />
              </ActionButton>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function RecordCard({ record, busy, act: pageAct }) {
  // Every action on this card refreshes this record alone rather than re-querying the page.
  // Bound once here so no call site can forget and reshuffle the list.
  const act = (key, run, message) => pageAct(key, run, message, { recordId: record.id, entityId: record.artist_id });
  const links = record.links || [];
  // Same split as the event-research candidates: the ones the agent was confident about are
  // normal options, the rest are folded away rather than dropped — a low-confidence link is real
  // information, just not an equal choice.
  const confident = links.filter((link) => (link.confidence ?? 0) >= LINK_CONFIDENCE_FLOOR);
  const unsure = links.filter((link) => (link.confidence ?? 0) < LINK_CONFIDENCE_FLOOR);
  const isOpen = record.status === "open";

  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-lg font-medium">
              {/* A router link, not `<a>`: a full navigation remounts the SPA and loses the tab,
                  the search, the page and the scroll position — everything about where the admin
                  was in the queue — with no working back button to the card they were reading. */}
              <EntityLink type="artist" id={record.artist_id}>
                {displayLabel(record.artist_preferred_name, record.artist_name) || record.artist_id}
              </EntityLink>
            </h2>
            <ProviderLabel preferred={record.artist_preferred_name} original={record.artist_name} />
            <ArtistFacts record={record} busy={busy} act={act} />
          </div>
          <Badge variant={isOpen ? "secondary" : "outline"}>{record.status}</Badge>
        </div>

        {links.length > 0 ? (
          <section className="space-y-2">
            <p className="flex items-center gap-2 text-sm font-medium">
              <Link2 className="h-4 w-4" /> Proposed links
            </p>
            {confident.map((link) => (
              <ProposedLink key={link.platform} record={record} link={link} busy={busy} act={act} isOpen={isOpen} />
            ))}
            {unsure.length > 0 && (
              <details className="rounded-md border p-2">
                <summary className="cursor-pointer text-xs text-muted-foreground">
                  {unsure.length} less certain link{unsure.length === 1 ? "" : "s"}
                </summary>
                <div className="mt-2 space-y-2">
                  {unsure.map((link) => (
                    <ProposedLink key={link.platform} record={record} link={link} busy={busy} act={act} isOpen={isOpen} />
                  ))}
                </div>
              </details>
            )}
          </section>
        ) : (
          <AbsentSection icon={Link2} title="Links" reason={absentLinksReason(record)} />
        )}

        {isOpen && <ManualLink record={record} busy={busy} act={act} />}

        {record.musicbrainz ? (
          <ProposedMusicbrainz record={record} busy={busy} act={act} isOpen={isOpen} />
        ) : (
          <AbsentSection icon={Fingerprint} title="MusicBrainz" reason={absentMusicbrainzReason(record)} />
        )}

        <div className="flex items-center justify-end gap-2 border-t pt-3">
          {!isOpen && (
            <span className="mr-auto text-xs text-muted-foreground">
              This record is {record.status}. Removing a link still works; reopen it to accept
              anything else.
            </span>
          )}
          {(isOpen
            ? [
                { status: "dismissed", label: "Dismiss", message: "Record dismissed" },
                { status: "resolved", label: "Done", message: "Record resolved", variant: "default" }
              ]
            : [{ status: "open", label: "Reopen", message: "Record reopened" }]
          ).map(({ status: next, label, message, variant }) => (
            <ActionButton
              key={next}
              busy={busy}
              actionKey={`${next}-${record.id}`}
              variant={variant}
              onClick={() => act(`${next}-${record.id}`, () => artistResearchApi.setStatus(record.id, next), message)}
            >
              {label}
            </ActionButton>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ArtistResearchPage() {
  const {
    records,
    pagination,
    status,
    page,
    setPage,
    searchInput,
    setSearchInput,
    search,
    loading,
    error,
    success,
    busy,
    load,
    act,
    changeStatus,
    runSearch,
    resetSearch
  } = useResearchQueue({ api: artistResearchApi, pageSize: PAGE_SIZE, entityLabel: "artist" });

  if (loading) return <QueueSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Sparkles className="h-5 w-5" /> Artist research
          </h1>
          <p className="text-sm text-muted-foreground">
            Links and MusicBrainz identities for the artists MusicBrainz gave us nothing for.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={Boolean(busy)}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StatusTabs status={status} onChange={changeStatus} />
        <SearchFilterForm
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={runSearch}
          onClear={resetSearch}
          placeholder="Search name or artist ID"
          loading={loading}
        />
        <span className="text-sm text-muted-foreground">{pagination.total} record(s)</span>
      </div>

      {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      <SuccessToast message={success} />

      {records.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
            <Inbox className="h-8 w-8" />
            <p>{search ? "No research records match this filter." : `No ${status} research records.`}</p>
          </CardContent>
        </Card>
      ) : (
        records.map((record) => <RecordCard key={record.id} record={record} busy={busy} act={act} />)
      )}

      <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />
    </div>
  );
}
